import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, Form, Message } from 'semantic-ui-react';
import { useAuth } from '../contexts/AuthContext';
import researchSplash from '../assets/rdh-splash.jpg';
import './PreLoginSplash.css';

// Break-glass password login is not shown on the home page. It is reachable only
// by navigating to a hidden marker, which auto-opens the staff login modal.
// (The backend still enforces ALLOW_LOCAL_LOGIN before accepting credentials.)
// Accept it as a path (/staff-login) or a hash (#staff-login) so it works on any
// host regardless of SPA-fallback configuration.
const STAFF_LOGIN_PATH = '/staff-login';

function isStaffLoginRoute() {
  const path = window.location.pathname.replace(/\/$/, '');
  const hash = (window.location.hash || '').replace(/^#/, '');
  return path === STAFF_LOGIN_PATH || hash === 'staff-login';
}

export default function PreLoginSplash() {
  const { login, verifyCode, loginWithNyc, ssoError, clearSsoError } = useAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // 2FA state
  const [step, setStep] = useState('credentials'); // 'credentials' | 'code'
  const [verificationToken, setVerificationToken] = useState(null);
  const [phoneLast4, setPhoneLast4] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const codeInputRef = useRef(null);

  // Auto-focus the code input when switching to the code step
  useEffect(() => {
    if (step === 'code') {
      // Small delay to allow the DOM to render
      setTimeout(() => codeInputRef.current?.focus(), 50);
    }
  }, [step]);

  // Hidden break-glass entry point: /staff-login (or #staff-login) opens the
  // password login modal, then scrubs the URL so it doesn't linger in history.
  useEffect(() => {
    if (isStaffLoginRoute()) {
      openLoginModal();
      window.history.replaceState(null, '', '/' + window.location.search);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetState = () => {
    setEmail('');
    setPassword('');
    setError(null);
    setStep('credentials');
    setVerificationToken(null);
    setPhoneLast4('');
    setSmsCode('');
    setSubmitting(false);
  };

  const openLoginModal = () => {
    resetState();
    setLoginModalOpen(true);
  };

  const closeLoginModal = () => {
    setLoginModalOpen(false);
    resetState();
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.verificationRequired) {
        setVerificationToken(result.verificationToken);
        setPhoneLast4(result.phoneLast4);
        setStep('code');
      } else {
        closeLoginModal();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyCode(verificationToken, smsCode);
      closeLoginModal();
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.error || 'Verification failed.';
      setError(message);
      // On 401 (expired/invalid token or wrong code), reset to credentials step
      if (status === 401) {
        setStep('credentials');
        setVerificationToken(null);
        setPhoneLast4('');
        setSmsCode('');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToSignIn = () => {
    setStep('credentials');
    setVerificationToken(null);
    setPhoneLast4('');
    setSmsCode('');
    setError(null);
  };

  return (
    <div className="prelogin-splash">
      <div
        className="prelogin-hero"
        role="img"
        aria-label="Research Data Hub"
        style={{ backgroundImage: `url(${researchSplash})` }}
      >
        <div className="prelogin-hero-overlay">
          <div className="prelogin-hero-content">
            <h1 className="prelogin-title">Research Data Hub</h1>
            <p className="prelogin-subtitle">Landmarks Preservation Commission</p>
            <p className="prelogin-description">
              Search, explore, and manage LPC's Research Department data. Create and analyze survey data, SHPO CRIS Data, LPC environmental review historic resource determinations, and Request for Evaluation (RFE) determinations. Sign in to continue.
            </p>
            {ssoError && (
              <Message negative onDismiss={clearSsoError} style={{ textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
                {ssoError}
              </Message>
            )}
            <Button primary size="large" className="prelogin-login-btn" onClick={loginWithNyc}>
              Sign in with NYC account
            </Button>
          </div>
        </div>
      </div>

      <Modal open={loginModalOpen} onClose={closeLoginModal} size="small" className="prelogin-modal">
        <Modal.Header>
          {step === 'credentials' ? 'Sign in' : 'Enter verification code'}
        </Modal.Header>
        <Modal.Content>
          {step === 'credentials' ? (
            <Form onSubmit={handleCredentialsSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && !submitting) handleCredentialsSubmit(e); }}>
              {error && (
                <Message negative onDismiss={() => setError(null)}>
                  {error}
                </Message>
              )}
              <Form.Input
                label="Email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e, { value }) => setEmail(value)}
                autoComplete="email"
              />
              <Form.Input
                label="Password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e, { value }) => setPassword(value)}
                autoComplete="current-password"
              />
              <Form.Button primary type="submit" content="Sign in" loading={submitting} disabled={submitting} />
            </Form>
          ) : (
            <Form onSubmit={handleCodeSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && !submitting && smsCode.length === 6) handleCodeSubmit(e); }}>
              {error && (
                <Message negative onDismiss={() => setError(null)}>
                  {error}
                </Message>
              )}
              <p>
                A verification code has been sent to your phone ending in <strong>****{phoneLast4}</strong>.
              </p>
              <Form.Input
                label="Verification code"
                placeholder="Enter 6-digit code"
                value={smsCode}
                onChange={(e, { value }) => {
                  // Only allow digits, max 6
                  const digits = value.replace(/\D/g, '').slice(0, 6);
                  setSmsCode(digits);
                }}
                input={{ ref: codeInputRef }}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
              />
              <Form.Button
                primary
                type="submit"
                content="Verify"
                loading={submitting}
                disabled={submitting || smsCode.length !== 6}
              />
              <Button
                basic
                type="button"
                content="Back to sign in"
                onClick={handleBackToSignIn}
                style={{ marginTop: '0.5rem' }}
              />
            </Form>
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
}
